import { IconProps } from "@/components-v2/Icon";
import {
  DCEvent,
  httpDelete,
  HttpEvent,
  httpGet,
  httpPost,
  httpPut,
  HttpResponse,
  navigate,
  showAlert,
  showError,
  showGeneralError,
  showSuccess,
} from "@/events/eventsDomain";
import { logError } from "@/utils/log";
import { isRegisterSubscription } from "@/payment/ApplicationFeatures";
import {
  adminFollowUpShareholders,
  adminFollowUpSignatures,
  adminOverview,
  companyUrl,
  navigateToProjectEvent,
  portfolioCompaniesUrl,
  portfolioUrl,
  shareholderOverview,
  superadminUrl,
} from "@/utils/navigation";
import { TabButtonProps } from "@/components-v2/Tabs";
import { Impersonation, User, GetUserSettingsResponse } from "@/types/UserInfo";
import {
  AdminItem,
  CompanyItem,
  failure,
  isOk,
  Ok,
  ok,
  pending,
  Portfolio,
  Result,
  SignDocumentCount,
  signer,
} from "@/common/common";
import {
  CompanyState,
  InvitationResponse,
  OnboardingCandidateResponse,
  PortfolioCompany,
  ShareholderRegisterStatementPortfolioStatusType,
} from "@/stores/PortfolioStore";
import { compact, identity, uniqBy } from "lodash";
import { AccessType } from "@/types/enums/AccessType";
import { TMessage } from "@/i18n/tt";
import { DecisionType } from "@/common/decisions";
import { GetSubscriptionStatusResponse } from "@/document/documentApi";
import { ViewDocumentSigningStatusDialogModel } from "@/dbot/summary/summaryDomain";
import {
  getCompanyItems,
  getEmailBody,
  SendForSigningState,
  signaturesApi,
  SignaturesContext,
  updateSignaturesStore,
} from "./signatures/signaturesDomain";
import {
  AcceptUserTermsAndUpdateEmailRequest,
  ActiveSubscribedCompanies,
  ConnectCompaniesRequest,
  initPortfolioCompaniesPage,
  mapToNotConnectedState,
  NewProjectCompaniesMode,
  PortfolioPageActions,
  RoleFilter,
} from "./companies/companiesDomain";
import { CompanyRole, SuggestedCompany } from "@/types/CompanyInfo";
import { useRootStore, useUserStore } from "@/stores/RootStoreContext";
import { useEffect, useMemo } from "react";
import { makeAutoObservable, runInAction } from "mobx";
import { orderlessPredicate, tokenizeFilterString } from "@/utils/filter";
import { ShareholderDTO } from "@/types/Shareholder";
import { InvitationType } from "@/types/Invitation";
import { OnboardingCandidateRoleType } from "@/types/enums/RoleType";
import {
  BatchPreviewModel,
  FileToPreview,
  toFileToPreview,
} from "@/components-v2/BatchPreviewDialog/batchPreviewDomain";
import { isNorwegianOrgNo, isSwedishOrgNo } from "@/utils/orgNo";
import { when } from "@/utils/functions";
import {
  BooleanFieldModel,
  createEmailField,
  LocalDateFieldModel,
  minLengthValidator,
  OptionFieldModel,
  RadioFieldModel,
  ShareholderItem,
  SignersForm,
  StringFieldModel,
} from "@/common/commonForms";
import { FileUploadConfig } from "@/fileUpload/fileUploadDomain";
import { AIClient } from "@/ai/AIAssistant";
import { ShareType } from "@/common/share";
import { findInitialUrl } from "@/common/customRoleNavigation";
import { Language } from "@/i18n/languages";
import { getFullName } from "@/utils/Roles";
import { RootStoreData } from "@/events/rootStoreData";
import { PaidSubscription } from "@/common/subscription";
import { isBlank } from "@/utils/strings";
import { toLocalDateISOString } from "@/utils/Dates";
import { toQueryString } from "@/utils/queryParams";
import {
  contentLabels,
  ExportContent,
  ExportLanguage,
  exportLanguageLabels,
  ExportShareholderData,
  ExportShareholderModel,
} from "@/shareholderRegister/shareholders/ExportShareholdersDomain";
import { downloadFile, getFilenameFromHeaders } from "@/api";
import {
  AddCompaniesToCustomRoleRequest,
  RemoveCompanyAccessFromCompanyRole,
} from "./contacts/customRoleDomain";
import { GetAdminsInCompaniesResponse, GetPortfolioCustomRoleRequest } from "@/types/Settings";
import { MeetingType } from "@/meetings/meetingsDomain";
import { CustomRole } from "@/common/customRole";
import { selectedDestinationFolderModel } from "@/dbot/common/commonDomain";
import { IconName } from "@/components-v2/iconMapping";
import { ModuleName } from "@/common/product";
import { GetContactsResponse } from "./contacts/contactsDomain";
import { Transaction } from "@/types/Transactions";
import { UpdatePersonField } from "./contacts/aggregatedContactDetailsDomain";
import { projectApi } from "@/dbot/common/projectApi";
import { ProjectNavigationState } from "@/dbot/project/projectDomain";
import { DocumentType } from "@/common/documents";
import { QuickFilter } from "@/components-v2/QuickFilterBar";

export const portfolioAllCompanies: PortfolioAllCompanies = {
  type: "all-companies",
  label: { key: "portfolio.all-companies" },
  icon: "globe",
};

export const portfolioWithAdminRole: PortfolioWithAdminRole = {
  type: "with-admin-role",
  label: { key: "portfolio.with-admin-role" },
  icon: "buildings",
};

export const portfolioWithBoardRole: PortfolioWithBoardRole = {
  type: "with-board-role",
  label: { key: "portfolio.with-board-role" },
  icon: "users-three",
};

export const portfolioMyInvestments: PortfolioMyInvestments = {
  type: "my-investments",
  label: { key: "portfolio.my-investments" },
  icon: "briefcase",
};

export const portfolioInstance = (portfolio: Portfolio): PortfolioInstance => ({
  type: "portfolio-instance",
  label: { translated: portfolio.name },
  portfolio,
});

export const isOwnerOfSelectedPortfolio = (
  portfolio: SelectedPortfolioOption,
  userExternalId: string | undefined,
): boolean =>
  portfolio.type === "portfolio-instance" && portfolio.portfolio.ownerId === userExternalId;

export type PortfolioAllCompanies = {
  type: "all-companies";
  label: { key: "portfolio.all-companies" };
  icon: IconProps["name"];
};

type PortfolioWithAdminRole = {
  type: "with-admin-role";
  label: { key: "portfolio.with-admin-role" };
  icon: IconProps["name"];
};

type PortfolioWithBoardRole = {
  type: "with-board-role";
  label: { key: "portfolio.with-board-role" };
  icon: IconProps["name"];
};

type PortfolioMyInvestments = {
  type: "my-investments";
  label: { key: "portfolio.my-investments" };
  icon: IconProps["name"];
};

export type PortfolioInstance = {
  type: "portfolio-instance";
  portfolio: Portfolio;
  label: TMessage;
};

export type SelectedPortfolioOption =
  | PortfolioAllCompanies
  | PortfolioWithAdminRole
  | PortfolioWithBoardRole
  | PortfolioMyInvestments
  | PortfolioInstance;

export type PortfolioType = SelectedPortfolioOption["type"];

export const mapRoleToPortfolioType = (role?: AccessType): PortfolioType => {
  switch (role) {
    case AccessType.ADMIN:
      return "with-admin-role";
    case AccessType.BOARD_MEMBER:
      return "with-board-role";
    case AccessType.SHAREHOLDER:
      return "my-investments";
    default:
      return "all-companies";
  }
};

export const mapToPortfolio = (
  type: PortfolioType,
  id: string | undefined,
  portfolios: Portfolio[],
): SelectedPortfolioOption => {
  switch (type) {
    case "with-admin-role":
      return portfolioWithAdminRole;
    case "with-board-role":
      return portfolioWithBoardRole;
    case "my-investments":
      return portfolioMyInvestments;
    case "portfolio-instance":
      return (
        when(
          portfolios.find((p) => p.id === id),
          portfolioInstance,
        ) ??
        portfolioInstance({
          id: id ?? "",
          name: "",
          createdAt: "",
          createdBy: "",
          ownerId: "",
          createdEmail: "",
        })
      );
    case "all-companies":
      return portfolioAllCompanies;
  }
};

export const extractPortfolioUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
  header?: PortfolioHeader,
): string =>
  extractPortfolioBaseUrl(selectedPortfolioOption) +
  "/" +
  ((header === "follow-up" ? "follow-up/signatures" : header) ?? "companies");

export const extractPortfolioCompaniesUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
): string => extractPortfolioBaseUrl(selectedPortfolioOption) + "/companies";

export const extractPortfolioFollowUpMattersUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
): string =>
  compact([extractPortfolioBaseUrl(selectedPortfolioOption), "follow-up", "matters"]).join("/");

export const extractPortfolioFollowUpSignaturesUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
): string =>
  compact([extractPortfolioBaseUrl(selectedPortfolioOption), "follow-up", "signatures"]).join("/");

export const extractPortfolioFollowUpCompanyInfoUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
): string =>
  compact([extractPortfolioBaseUrl(selectedPortfolioOption), "follow-up", "company-info"]).join(
    "/",
  );

export const extractPortfolioFollowUpShareholdersUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
  activeTab?: string,
  contextCompany?: string,
): string => {
  return contextCompany
    ? compact([adminFollowUpShareholders(contextCompany), activeTab]).join("/")
    : compact([
        extractPortfolioBaseUrl(selectedPortfolioOption),
        "follow-up",
        "shareholders",
        activeTab,
      ]).join("/");
};

export const extractFollowUpShareholdersUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
): string =>
  compact([extractPortfolioBaseUrl(selectedPortfolioOption), "follow-up", "shareholders"]).join(
    "/",
  );

export const extractPortfolioContactsUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
): string => compact([extractPortfolioBaseUrl(selectedPortfolioOption), "contacts"]).join("/");

export const extractPortfolioContactsAdminUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
): string =>
  compact([extractPortfolioBaseUrl(selectedPortfolioOption), "contacts/admins"]).join("/");

export const extractPortfolioAdminsUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
  externalId: string,
): string =>
  compact([extractPortfolioBaseUrl(selectedPortfolioOption), "contacts/admins", externalId]).join(
    "/",
  );

export const extractPortfolioShareholderUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
  shareholderId: number,
  activeTab?: string,
): string =>
  compact([
    extractPortfolioBaseUrl(selectedPortfolioOption),
    "shareholder",
    shareholderId,
    activeTab,
  ]).join("/");

export const extractPortfolioCustomRoleUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
  customRoleId: string,
): string =>
  compact([extractPortfolioContactsUrl(selectedPortfolioOption), "custom-role", customRoleId]).join(
    "/",
  );

export const extractPortfolioCustomRoleDetailsUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
  customRoleId: string,
  externalId: string,
): string =>
  compact([
    extractPortfolioBaseUrl(selectedPortfolioOption),
    "contacts/custom-role",
    customRoleId,
    "details",
    externalId,
  ]).join("/");

export const extractPortfolioInstanceUrl = (id: string): string =>
  extractPortfolioBaseUrl({
    type: "portfolio-instance",
    portfolio: { id, name: "", createdAt: "", createdBy: "", ownerId: "", createdEmail: "" },
    label: { translated: "" },
  });

type RoleBasedPortfolioType = Exclude<PortfolioType, "portfolio-instance">;

const portfolioUrlSegments: Record<RoleBasedPortfolioType, string> = {
  "all-companies": "all",
  "with-admin-role": AccessType.ADMIN.toLowerCase(),
  "with-board-role": AccessType.BOARD_MEMBER.toLowerCase(),
  "my-investments": AccessType.SHAREHOLDER.toLowerCase(),
};

export type PortfolioRouteTarget =
  | { portfolioType: RoleBasedPortfolioType; portfolioId?: undefined }
  | { portfolioType: "portfolio-instance"; portfolioId: string };

export const parsePortfolioUrlSegment = (segment: string): PortfolioRouteTarget => {
  const portfolioType = (Object.keys(portfolioUrlSegments) as RoleBasedPortfolioType[]).find(
    (type) => portfolioUrlSegments[type] === segment,
  );
  return portfolioType
    ? { portfolioType }
    : { portfolioType: "portfolio-instance", portfolioId: segment };
};

export const extractPortfolioBaseUrl = (selectedPortfolioOption: SelectedPortfolioOption): string =>
  `${portfolioUrl}/${
    selectedPortfolioOption.type === "portfolio-instance"
      ? selectedPortfolioOption.portfolio.id
      : portfolioUrlSegments[selectedPortfolioOption.type]
  }`;

export const toPortfolioApiUrl = (selectedPortfolioOption: SelectedPortfolioOption): string => {
  const format = (part: string) => "/portfolio" + part.toLowerCase();
  switch (selectedPortfolioOption.type) {
    case "all-companies":
      return format("");
    case "with-admin-role":
      return format(`/role/${AccessType.ADMIN}`);
    case "with-board-role":
      return format(`/role/${AccessType.BOARD_MEMBER}`);
    case "my-investments":
      return format(`/role/${AccessType.SHAREHOLDER}`);
    case "portfolio-instance":
      return format(`/${selectedPortfolioOption.portfolio.id}`);
  }
};

export const toPortfolioContactsApiUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
): string => {
  const format = (part: string) => "/portfolio" + part.toLowerCase();
  switch (selectedPortfolioOption.type) {
    case "all-companies":
      return format("");
    case "with-admin-role":
      return format("");
    case "with-board-role":
      return format(`/contacts/${AccessType.BOARD_MEMBER}`);
    case "my-investments":
      return format(`/contacts/${AccessType.SHAREHOLDER}`);
    case "portfolio-instance":
      return format(`/${selectedPortfolioOption.portfolio.id}`);
  }
};

export type PortfolioSettingsActions = {
  openPortfolioSettings: () => void;
  closePortfolioSettings: () => void;
  savePortfolioSettings: () => void;
  confirmDissolvePortfolio: () => void;
  closeConfirmDissolvePortfolio: () => void;
  dissolvePortfolio: () => void;
};

export type PortfolioSettingsState = {
  isPortfolioSettingsOpen?: boolean;
  portfolioSettingsModel: PortfolioSettingsModel;
  portfolioSettingsUpdateResult?: Result<TMessage, TMessage>;
  confirmDissolvePortfolio?: boolean;
  portfolioCreationInfo?: TMessage;
};

export type EnterpriseAccountItem = {
  id: string;
  name: string;
};

export type ReadyState = {
  type: "ready";
  portfolioSearchModel: StringFieldModel;
  companiesTab: PortfolioCompaniesTab;
  portfolioResponse: PortfolioResponse;
  companiesTabs: TabButtonProps[];
  isAddCompanyDialogOpen?: boolean;
  isNewProjectDialogOpen?: boolean;
  selectedCompanies: string[];
  userCompaniesInPortfolio: PortfolioCompany[];
  availableCompanies: PortfolioCompany[];
  addCompaniesResult?: Result<TMessage, TMessage>;
  signingStatusDialogModel?: ViewDocumentSigningStatusDialogModel;
  createProjectResult?: Result<TMessage, TMessage>;
  checkedDecisions: DecisionType[];
  companiesMode: RadioFieldModel<NewProjectCompaniesMode>;
  activeHeader: PortfolioHeader;
  currentUrl: string;
  totalSignDocumentCount?: number;
  portfolio: SelectedPortfolioOption;
  documentPreview?: { filename: string; url: string };
  removeCompanyFromPortfolio?: PortfolioCompany;
  removeFromPortfolioResult?: Result<TMessage, TMessage>;
  moveToPortfolio?: PortfolioCompany;
  destinationPortfolio?: Portfolio;
  moveToPortfolioResult?: Result<TMessage, TMessage>;
  signDocumentCounts?: SignDocumentCount[];
  norwegianCompanySearchDialogState?: CompanySearchDialogState;
  swedishCompanySearchDialogState?: CompanySearchDialogState;
  openInvitationRequest?: InvitationResponse;
  notConnected: NotConnectedTabState;
  companySubscription?: ActiveSubscribedCompanies;
  adminsCompanyCount?: number;
  isLoadingCustomRole: boolean;
  customRoleItem: GetPortfolioCustomRoleRequest;
  customRoleFilter?: RadioFieldModel<CustomRole | null>;
  arStatusFilter?: RadioFieldModel<ShareholderRegisterStatementPortfolioStatusType | null>;
  userSettings: GetUserSettingsResponse;
  hoveredTransaction?: Result<Transaction>;
  showTransferOwnershipDialog?: boolean;
  ownershipUserResult?: Result<GetAdminsInCompaniesResponse>;
  isLoadingOwnershipTransfer?: boolean;
  transferOwnershipModel?: RadioFieldModel<AdminItem | undefined>;
  companySearchFieldModel?: StringFieldModel;
  showOnlySelected?: boolean;
  primaryCompanyOrgNo?: string;
  signShareholderRegisters?: SignShareholderRegistersForm;
  quickFilters: QuickFilter[];
} & PortfolioSettingsState;

export type NotConnectedTabState = {
  showConnectAllDialog: boolean;
  connectAllCompaniesResult?: Result;
  subsidiaries: ConnectSubsidiaryItemState[];
  rolesFilter: RadioFieldModel<RoleFilter>;
  companies: PortfolioCompany[];
};

export type CompanySearchDialogState = {
  searchResult?: Result<SuggestedCompany[]>;
  askForPermissionResult?: Result;
  onboardingCandidatesResult?: Result<OnboardingCandidateResponse>;
  companyNameSearchField: StringFieldModel;
  accessTypeModel?: RadioFieldModel<RequestAccessType | null>;
  personalNoteModel?: StringFieldModel;
  selectedCompany?: SuggestedCompany;
  receiverEmailAddress: StringFieldModel;
  searchField: StringFieldModel;
  isRenderSearchResult?: boolean;
  tooManyHits?: boolean;
  disableSearch?: boolean;
  sourceOrgNoName?: string;
  ownershipInPercent?: number;
};

export const requestAccessTypes = ["shareholder-portal", "board-portal", "admin"] as const;

export type RequestAccessType = (typeof requestAccessTypes)[number];

export const requestAccessTypeIcons: Record<RequestAccessType, IconName> = {
  "shareholder-portal": "notebook",
  "board-portal": "calendar",
  admin: "buildings",
};

export const requestAccessTypeTitleLabel: Record<RequestAccessType, TMessage> = {
  "shareholder-portal": { key: "requestAccessTypes.shareholder-portal.title" },
  "board-portal": { key: "requestAccessTypes.board-portal.title" },
  admin: { key: "requestAccessTypes.admin.title" },
};

export const requestAccessTypeDescriptionLabel: Record<RequestAccessType, TMessage> = {
  "shareholder-portal": { key: "requestAccessTypes.shareholder-portal.description" },
  "board-portal": { key: "requestAccessTypes.board-portal.description" },
  admin: { key: "requestAccessTypes.admin.description" },
};

type PortfolioSettingsModel = {
  name: StringFieldModel;
};

export type LoadingPortfolioState = {
  type: "loading-portfolio";
  portfolio: SelectedPortfolioOption;
  companiesTab?: PortfolioCompaniesTab;
  activeHeader?: PortfolioHeader;
  signaturesTab?: PortfolioSignaturesTab;
};

export type InvalidPortfolioIdState = {
  type: "invalid-portfolioid";
  portfolio: SelectedPortfolioOption;
  companiesTab?: PortfolioCompaniesTab;
  activeHeader?: PortfolioHeader;
  signaturesTab?: PortfolioSignaturesTab;
};

export type PortfolioPageState = ReadyState | LoadingPortfolioState | InvalidPortfolioIdState;

export type LoadingPortfolioSignaturesState = {
  type: "loading-portfolio-signatures";
  portfolio: SelectedPortfolioOption;
  signaturesTab?: PortfolioSignaturesTab;
};

export type PortfolioSignaturesResponse = {
  signatures: SignaturesContext;
  hasAdminRole: boolean;
  portfolio?: Portfolio;
  companiesInPortfolio: CompanyItem[];
  signDocumentCount: number;
  followUpMattersCount: number;
  companyInfoCount: number;
  followUpShareholdersCount: number;
  otherPendingDocumentsCount: number;
};

export type PortfolioShareholdersResponse = {
  shareholders: ShareholderDTO[];
  shareTypes: ShareType[];
  companyItems: CompanyItem[];
  signDocumentCount: number;
  followUpMattersCount: number;
  companyInfoCount: number;
  followUpShareholdersCount: number;
  importExcelConfig: FileUploadConfig;
};

export type PortfolioSignaturesReadyState = {
  type: "portfolio-signatures-ready";
  signatureSearchModel: StringFieldModel;
  signaturesTab?: PortfolioSignaturesTab;
  signaturesTabs: TabButtonProps[];
  signaturesResponse: PortfolioSignaturesResponse;
  signingStatusDialogModel?: ViewDocumentSigningStatusDialogModel;
  sendForSigningState?: SendForSigningState;
  portfolio: SelectedPortfolioOption;
  displayGlobalSignatureEmptyState: boolean;
  documentPreview?: BatchPreviewModel;
  companyFilter: RadioFieldModel<CompanyItem | null>;
  contextCompany?: string;
  documentSignaturesMessage?: TMessage;
  duplicateFileUploading?: TMessage;
  isEmailVerified?: boolean;
} & PortfolioSettingsState;

export type PortfolioSignaturesState =
  | PortfolioSignaturesReadyState
  | LoadingPortfolioSignaturesState
  | InvalidPortfolioIdState;

export type PortfolioPageStore = {
  portfolioState?: PortfolioPageState;
  portfolioSignatures?: PortfolioSignaturesState;
};

export type PortfolioPageDispatcher = (events: DCEvent[]) => void;

export const portfolioApi = {
  getPortfolio: ({
    portfolio,
    onSuccess,
    onFailure,
  }: {
    portfolio: SelectedPortfolioOption;
    onSuccess: (res: HttpResponse<PortfolioResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpGet({
      url: toPortfolioApiUrl(portfolio),
      onSuccess,
      onFailure,
    }),

  addCompaniesToPortfolio: ({
    portfolioId,
    companies,
    onSuccess,
    onFailure,
  }: {
    portfolioId: string;
    companies: string[];
    onSuccess: (res: HttpResponse<PortfolioResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }): DCEvent =>
    httpPost({
      url: `${portfolioUrl}/${portfolioId}/add-companies`,
      data: companies,
      onSuccess,
      onFailure,
    }),

  starCompany: ({
    orgNo,
    accessType,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    accessType: AccessType;
    onSuccess: (res: HttpResponse<PortfolioResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }): DCEvent =>
    httpPut({
      url: `/user/star-company/${orgNo}/${accessType}`,
      onSuccess,
      onFailure,
    }),

  unstarCompany: ({
    orgNo,
    accessType,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    accessType: AccessType;
    onSuccess: (res: HttpResponse<PortfolioResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }): DCEvent =>
    httpPut({
      url: `/user/unstar-company/${orgNo}/${accessType}`,
      onSuccess,
      onFailure,
    }),

  removeFromPortfolio: ({
    company,
    onSuccess,
    onFailure,
  }: {
    company: PortfolioCompany;
    onSuccess: (res: HttpResponse<PortfolioResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: `${portfolioUrl}/remove-company-from-portfolio`,
      data: { orgNo: company.organisationNumber },
      onSuccess,
      onFailure,
    }),

  moveToPortfolio: ({
    company,
    portfolioId,
    onSuccess,
    onFailure,
  }: {
    company: PortfolioCompany;
    portfolioId: string;
    onSuccess: (res: HttpResponse<PortfolioResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: `${portfolioUrl}/move-company-to-portfolio`,
      data: { orgNo: company.organisationNumber, portfolioId },
      onSuccess,
      onFailure,
    }),

  updatePortfolioSettings: ({
    portfolioId,
    name,
    onSuccess,
    onFailure,
  }: {
    portfolioId: string;
    name: string;
    onSuccess: (res: HttpResponse<PortfolioResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPut({
      url: `${portfolioUrl}/${portfolioId}/settings`,
      data: { name },
      onSuccess,
      onFailure,
    }),

  deletePortfolio: ({
    portfolioId,
    onSuccess,
    onFailure,
  }: {
    portfolioId: string;
    onSuccess: (res: HttpResponse<PortfolioResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpDelete({
      url: `${portfolioUrl}/${portfolioId}`,
      onSuccess,
      onFailure,
    }),

  findOnboardingCandidates: ({
    orgNo,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    onSuccess: (res: HttpResponse<OnboardingCandidateResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpGet({
      url: `/company-roles/find-onboarding-candidates/${orgNo}`,
      onSuccess,
      onFailure,
    }),

  searchCompanies: ({
    query,
    onSuccess,
    onFailure,
  }: {
    query: string;
    onSuccess: (res: HttpResponse<SuggestedCompany[]>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => httpGet({ url: `/companysearch${toQueryString({ q: query })}`, onSuccess, onFailure }),

  sendAskForPermissionRequest: ({
    data,
    onSuccess,
    onFailure,
  }: {
    data: {
      organisationNumber: string;
      companyName: string;
      email: string;
      autoonboard: boolean;
      requestAccessType: RequestAccessType;
      personalNoteText: string;
    };
    onSuccess: (res: HttpResponse<void>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => httpPost({ url: `/invitation/ask-for-permission`, data, onSuccess, onFailure }),

  searchSwedishCompanies: ({
    query,
    onSuccess,
    onFailure,
  }: {
    query: string;
    onSuccess: (res: HttpResponse<SuggestedCompany[]>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => httpGet({ url: `/search-swedish-companies?q=${query}`, onSuccess, onFailure }),

  connectAllCompanies: ({
    data,
    onSuccess,
    onFailure,
  }: {
    data: ConnectCompaniesRequest;
    onSuccess: (res: HttpResponse<void>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => httpPost({ url: "/portfolio/connect-companies", data, onSuccess, onFailure }),

  acceptUserTermsAndUpdateEmail: ({
    data,
    onSuccess,
    onFailure,
  }: {
    data: AcceptUserTermsAndUpdateEmailRequest;
    onSuccess: (res: HttpResponse<void>, current: RootStoreData) => DCEvent[];
    onFailure: (err: unknown, current: RootStoreData) => DCEvent[];
  }) => httpPost({ url: "/portfolio/accept-terms", data, onSuccess, onFailure }),

  exportShareholderRegisterBatch: ({
    data,
    onSuccess,
    onFailure,
  }: {
    data: ExportShareholderData;
    onSuccess: (res: HttpResponse<Blob>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: `/shareholder/exports`,
      responseType: "blob",
      data,
      onSuccess,
      onFailure,
    }),

  addCompaniesToCustomRole: ({
    body,
    onSuccess,
    onFailure,
  }: {
    body: AddCompaniesToCustomRoleRequest;
    onSuccess: (res: HttpResponse<void>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: "/portfolio/add-companies-to-custom-role",
      data: body,
      onSuccess,
      onFailure,
    }),

  removeCompanyAccessFromCustomRole: ({
    body,
    onSuccess,
    onFailure,
  }: {
    body: RemoveCompanyAccessFromCompanyRole;
    onSuccess: (res: HttpResponse<void>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: "portfolio/remove-company-access-from-custom-role",
      data: body,
      onSuccess,
      onFailure,
    }),

  getCustomRoleItems: ({
    portfolio,
    portfolioId,
    onSuccess,
    onFailure,
  }: {
    portfolio: SelectedPortfolioOption;
    portfolioId?: string;
    onSuccess: (res: HttpResponse<GetPortfolioCustomRoleRequest>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => {
    const url =
      portfolioId === undefined
        ? `${toPortfolioContactsApiUrl(portfolio)}/custom-roles`
        : `/portfolio/custom-roles?portfolioId=${portfolioId}`;
    return httpGet({
      url,
      onSuccess,
      onFailure,
    });
  },

  getCompanyRoles: ({
    portfolio,
    onSuccess,
    onFailure,
  }: {
    portfolio: SelectedPortfolioOption;
    onSuccess: HttpEvent<GetContactsResponse>["onSuccess"];
    onFailure: HttpEvent<GetContactsResponse>["onFailure"];
  }): HttpEvent<GetContactsResponse> => {
    const url = `${toPortfolioContactsApiUrl(portfolio)}/company-roles`;
    return httpGet({
      url,
      onSuccess,
      onFailure,
    });
  },

  getLastTransactionById: ({
    id,
    onSuccess,
    onFailure,
  }: {
    id: number;
    onSuccess: (res: HttpResponse<Transaction>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => {
    return httpGet({
      url: `/transactionlog/${id}`,
      onSuccess,
      onFailure,
    });
  },

  updatePersonField: ({
    body,
    onSuccess,
    onFailure,
  }: {
    body: UpdatePersonField;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => {
    return httpPost({
      url: "/portfolio/update-person-fields",
      data: body,
      onSuccess,
      onFailure,
    });
  },

  getPortfolioAdminsForTransfer: ({
    portfolio,
    onSuccess,
    onFailure,
  }: {
    portfolio: SelectedPortfolioOption;
    onSuccess: (res: HttpResponse<GetAdminsInCompaniesResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }): HttpEvent => {
    return httpGet({ url: `${toPortfolioContactsApiUrl(portfolio)}/admins`, onSuccess, onFailure });
  },

  updatePortfolioOwnership: ({
    portfolioId,
    externalId,
    onSuccess,
    onFailure,
  }: {
    portfolioId?: string;
    externalId: string;
    onSuccess: (res: HttpResponse<PortfolioResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }): HttpEvent => {
    return httpPost({
      url: `/portfolio/${portfolioId}/update-ownership`,
      data: { externalId },
      onSuccess,
      onFailure,
    });
  },
} as const;

export type GetPortfolioCompanySignersResponse = {
  shareholders: ShareholderItem[];
  companyRoles: CompanyRole[];
};

export type PortfolioCompaniesTab =
  | "all"
  | "starred"
  | "not-connected"
  | "beneficial-owners"
  | "ar-status";
export type PortfolioSignaturesTab = "pending" | "signed" | "sent" | "sealed";
export type PortfolioHeader = "search" | "companies" | "follow-up" | "contacts" | "help-center";
export type PortfolioContactsTab = "shareholders" | "admins" | "board";

export const navigateToCompaniesTabAction = (
  companiesTab: PortfolioCompaniesTab,
  portfolio: SelectedPortfolioOption,
): DCEvent => {
  const url = extractPortfolioCompaniesUrl(portfolio);
  return navigate(`${url}/${companiesTab}`, { replace: true });
};

export const navigateToPortfolioCompaniesAction = (portfolio: SelectedPortfolioOption): DCEvent =>
  navigate(extractPortfolioCompaniesUrl(portfolio));

export const navigateToSignaturesTabAction = (
  portfolio: SelectedPortfolioOption,
  signaturesTab: PortfolioSignaturesTab,
): DCEvent => {
  const url = extractPortfolioFollowUpSignaturesUrl(portfolio);
  return navigate(`${url}/${signaturesTab}`);
};

export const navigateToSignaturesTabInContextCompany = (
  signaturesTab: PortfolioSignaturesTab,
  orgNo: string,
): DCEvent => {
  return navigate(`${adminFollowUpSignatures(orgNo)}/${signaturesTab}`);
};

export const companiesInPortfolio = (data: PortfolioResponse): PortfolioCompany[] =>
  data.userCompanies.filter(
    (c) =>
      !data.portfolio ||
      data.companyItems.some((i) => i.organisationNumber === c.organisationNumber),
  );

export const subsidiariesToPortfolioCompany = (
  subsidiaries: SubsidiaryItem[],
  companies: CompanyItem[],
): PortfolioCompany[] =>
  subsidiaries.map((s) => ({
    id: s.organizationNumber,
    organisationNumber: s.organizationNumber,
    name: s.name,
    isCustomer: false,
    accessType: AccessType.NONE,
    state: CompanyState.DRAFT,
    starred: false,
    ceoName: s.ceoName,
    chairPersonName: s.chairPersonName,
    isCEO: s.isCEO,
    isChairperson: s.isChairperson,
    isBoardmember: s.isBoardmember,
    isShareholder: s.isShareholder,
    sourceOrgNoName: companies.find((c) => c.organisationNumber === s.sourceOrgNo)?.companyName,
    ownershipInPercent: s.ownershipInPercent,
    currency: s.currency,
  }));

export function extractUserCompaniesInPortfolio(
  data: PortfolioResponse,
  activeTab: PortfolioCompaniesTab,
): PortfolioCompany[] {
  const all = companiesInPortfolio(data);
  switch (activeTab) {
    case "all":
      return all.filter((c) => c.isCustomer);
    case "not-connected":
      return all;
    case "starred":
      return all.filter((c) =>
        data.userSettings.starredCompanies?.some(
          (s) => s.organisationNumber === c.organisationNumber && s.accessType === c.accessType,
        ),
      );
    case "beneficial-owners":
      return all.filter(
        (c) =>
          c.isCustomer &&
          c.accessType === AccessType.ADMIN &&
          isNorwegianOrgNo(c.organisationNumber),
      );
    case "ar-status":
      return all.filter(
        (c) =>
          c.isCustomer &&
          c.accessType === AccessType.ADMIN &&
          isNorwegianOrgNo(c.organisationNumber),
      );
  }
}

export function showPortfolioContacts(portfolio: SelectedPortfolioOption) {
  return portfolio.type !== "with-board-role" && portfolio.type !== "my-investments";
}

export const mapToCompaniesTab = (companiesTab?: string): PortfolioCompaniesTab | undefined => {
  switch (companiesTab) {
    case "all":
      return "all";
    case "starred":
      return "starred";
    case "not-connected":
      return "not-connected";
    case "beneficial-owners":
      return "beneficial-owners";
    case "ar-status":
      return "ar-status";
    default:
      return undefined;
  }
};

export const mapToSignaturesTab = (signaturesTab?: string): PortfolioSignaturesTab | undefined =>
  signaturesTab === "pending"
    ? "pending"
    : signaturesTab === "signed"
      ? "signed"
      : signaturesTab === "sent"
        ? "sent"
        : signaturesTab === "sealed"
          ? "sealed"
          : undefined;

export const mapToContactsTab = (contactsTab?: string): PortfolioContactsTab | undefined =>
  contactsTab === "shareholders"
    ? "shareholders"
    : contactsTab === "admins"
      ? "admins"
      : contactsTab === "board"
        ? "board"
        : undefined;

export const mapToHeader = (header?: string): PortfolioHeader => {
  switch (header) {
    case "search":
      return "search";
    case "contacts":
      return "contacts";
    case "follow-up":
      return "follow-up";
  }
  return "companies";
};

export type InitPortfolioCompaniesPageProps = {
  dispatch: PortfolioPageDispatcher;
  currentUrl: string;
  portfolio: SelectedPortfolioOption;
  activeHeader?: PortfolioHeader;
  companiesTab?: PortfolioCompaniesTab;
  companiesMode?: RadioFieldModel<NewProjectCompaniesMode>;
  signDocumentCounts?: SignDocumentCount[];
  store: PortfolioPageStore;
  force?: boolean;
  language: Language;
};

export const isSamePortfolio = (
  a: SelectedPortfolioOption,
  b: SelectedPortfolioOption,
): boolean => {
  switch (true) {
    case a.type === "portfolio-instance" && b.type === "portfolio-instance":
      return a.portfolio.id === b.portfolio.id;
    default:
      return a.type === b.type;
  }
};

export type PortfolioSubscription = Pick<
  PaidSubscription,
  "organisationNumber" | "productName" | "modules" | "allowsDbot"
>;

export type PortfolioResponse = {
  portfolio?: Portfolio;
  portfolios: Portfolio[];
  user: {
    name: string;
    company?: string;
    initials: string;
    email?: string;
    impersonation?: Impersonation;
    isEmailVerified: boolean;
    acceptedTerms: boolean;
    externalId: string;
  };
  companyItems: CompanyItem[];
  userCompanies: PortfolioCompany[];
  userSettings: GetUserSettingsResponse;
  incompletedInvitationRequests: InvitationResponse[];
  subsidiaries: SubsidiaryItem[];
  signDocumentCount: number;
  followUpMattersCount: number;
  companyInfoCount: number;
  followUpShareholdersCount: number;
  otherPendingDocumentsCount: number;
  activeSubscriptionCompanies?: PortfolioSubscription[];
};

export type SubsidiaryItem = {
  sourceOrgNo: string;
  organizationNumber: string;
  name: string;
  ownershipInPercent: number;
  ownershipType: string;
  ceoName: string;
  chairPersonName: string;
  isCEO: boolean;
  isChairperson: boolean;
  isBoardmember: boolean;
  isShareholder: boolean;
  currency: string;
};

export type ConnectSubsidiaryItemState = {
  subsidiary: SubsidiaryItem;
  receiverEmail: StringFieldModel;
  checked: boolean;
};

export const openCompanyDialog = (state: ReadyState): DCEvent[] => [
  updateStore(state, { isAddCompanyDialogOpen: true, addCompaniesResult: undefined }),
];

export const newProjectDialog = (state: ReadyState): DCEvent[] => [
  updateStore(state, { isNewProjectDialogOpen: true }),
];

export type SignShareholderRegistersForm = {
  format: RadioFieldModel<ExportContent>;
  language: RadioFieldModel<ExportLanguage>;
  date: LocalDateFieldModel;
};

const signShareholderRegistersForm = (): SignShareholderRegistersForm => ({
  format: new RadioFieldModel<ExportContent>({
    value: ExportContent.ALL_DETAILS,
    options: [ExportContent.ALL_DETAILS, ExportContent.PUBLIC],
    labelFn: (item) => contentLabels[item],
    idFn: identity,
  }),
  language: new RadioFieldModel({
    value: ExportLanguage.NO,
    options: Object.values(ExportLanguage),
    labelFn: (item) => exportLanguageLabels[item],
    idFn: identity,
  }),
  date: new LocalDateFieldModel({ value: toLocalDateISOString(Date.now()) }),
});

export const openSignShareholderRegistersDialog = (state: ReadyState): DCEvent[] => [
  updateStore(state, {
    signShareholderRegisters: signShareholderRegistersForm(),
    selectedCompanies: toCompaniesWithAdminDbotAccess(state).map((c) => c.organisationNumber),
    companySearchFieldModel: new StringFieldModel(),
    showOnlySelected: false,
    createProjectResult: undefined,
  }),
];

export const closeSignShareholderRegistersDialog = (state: ReadyState): DCEvent[] => [
  updateStore(state, {
    signShareholderRegisters: undefined,
    selectedCompanies: [],
  }),
];

export const createShareholderRegisterSigningProject = (
  state: ReadyState,
  form: SignShareholderRegistersForm,
  documentLabel: string,
): DCEvent[] => {
  const batchCompanies = state.selectedCompanies;
  const primaryOrgNo =
    state.primaryCompanyOrgNo && batchCompanies.includes(state.primaryCompanyOrgNo)
      ? state.primaryCompanyOrgNo
      : batchCompanies[0];
  const date = form.date.validatedValue ?? "";
  return [
    updateStore(state, { createProjectResult: pending() }),
    projectApi.createProject(
      {
        orgNo: primaryOrgNo,
        batchCompanies,
        projectName: [date, documentLabel].join(" "),
        documents: [DocumentType.custom_document],
        customDocumentType: "blob",
        documentSource: {
          type: "shareholderRegister",
          format: form.format.value,
          language: form.language.value,
          date,
        },
      },
      {
        onSuccess: (res) => [
          navigateToProjectEvent(
            { orgNo: primaryOrgNo, projectId: res.data.projectId },
            recipientsPanelNavigationState(res.data.documentId),
          ),
          ...closeSignShareholderRegistersDialog(state),
          updateStore(state, { createProjectResult: ok({ key: "general-success" }) }),
        ],
        onFailure: () => [
          updateStore(state, { createProjectResult: failure({ key: "general-error" }) }),
        ],
      },
    ),
  ];
};

const recipientsPanelNavigationState = (documentId?: string): ProjectNavigationState => ({
  type: "project-navigation-state",
  view: documentId ? { detailsPanel: { type: "custom", id: "recipients", documentId } } : undefined,
});

export const closeProjectDialog = (state: ReadyState): DCEvent[] => [
  updateStore(state, {
    isNewProjectDialogOpen: false,
    checkedDecisions: [],
    selectedCompanies: [],
  }),
];

export const createBatchProject = (state: ReadyState, projectName: string): DCEvent[] => {
  const batchCompanies = state.selectedCompanies.filter((f) => {
    return state.availableCompanies.map((c) => f.includes(c.organisationNumber));
  });
  const primaryOrgNo =
    state.primaryCompanyOrgNo && batchCompanies.includes(state.primaryCompanyOrgNo)
      ? state.primaryCompanyOrgNo
      : batchCompanies[0];
  return [
    updateStore(state, { createProjectResult: pending() }),
    projectApi.createProject(
      {
        orgNo: primaryOrgNo,
        batchCompanies,
        projectName,
        decisions: state.checkedDecisions.map((d) => ({ decisionType: d })),
      },
      {
        onSuccess: (res) => [
          {
            type: "navigate",
            url: `/company/${primaryOrgNo}/projects/${res.data.projectId}`,
          },
          updateStore(state, { createProjectResult: ok({ key: "general-success" }) }),
        ],
        onFailure: () => [
          updateStore(state, { createProjectResult: failure({ key: "general-error" }) }),
        ],
      },
    ),
  ];
};

export const closeCompanyDialog = (state: ReadyState): DCEvent[] => [
  updateStore(state, { isAddCompanyDialogOpen: false }),
];

export const toggleDecision = (state: ReadyState, decisionType: DecisionType): DCEvent[] => {
  const index = state.checkedDecisions.indexOf(decisionType);
  const checkedDecisions =
    index > -1
      ? state.checkedDecisions.filter((d) => d !== decisionType)
      : state.checkedDecisions.concat(decisionType);
  return [updateStore(state, { checkedDecisions })];
};

export const toggleCompanyInAddDialog = (state: ReadyState, orgNo: string): DCEvent[] => {
  const isSelected = state.selectedCompanies.indexOf(orgNo) > -1;
  const selectedCompanies = isSelected
    ? state.selectedCompanies.filter((c) => c !== orgNo)
    : [...state.selectedCompanies, orgNo];
  return [updateStore(state, { selectedCompanies })];
};

export const addCompaniesToPortfolio = (state: ReadyState, onSuccess: DCEvent[]): DCEvent[] => {
  return [
    updateStore(state, { addCompaniesResult: pending() }),
    portfolioApi.addCompaniesToPortfolio({
      portfolioId: state.portfolioResponse.portfolio!.id,
      companies: state.selectedCompanies,
      onSuccess: () => [
        ...closeCompanyDialog(state),
        ...onSuccess,
        {
          type: "show-alert",
          message: {
            key: "portfolio.add-companies-success",
            params: { count: state.selectedCompanies.length },
          },
          messageType: "success",
        },
      ],
      onFailure: () => [
        updateStore(state, { addCompaniesResult: failure({ key: "general-error" }) }),
      ],
    }),
  ];
};

const createCompanySearchDialogState = (): CompanySearchDialogState => ({
  companyNameSearchField: new StringFieldModel({ required: true }),
  accessTypeModel: new RadioFieldModel<RequestAccessType | null>({
    value: null,
    options: [...requestAccessTypes],
    idFn: identity,
    labelFn: (item) => ({ translated: item ? item : "null" }),
  }),
  personalNoteModel: new StringFieldModel({ required: true }),
  receiverEmailAddress: createEmailField({ required: true }),
  searchField: new StringFieldModel({ required: true, validator: minLengthValidator(3) }),
});

export const selectCompanyFromSearchResult = (
  state: ReadyState,
  selectedCompany: SuggestedCompany | undefined,
): DCEvent[] => {
  state.norwegianCompanySearchDialogState?.companyNameSearchField.setInputValue(
    selectedCompany?.name ?? "",
  );
  const updated = {
    selectedCompany,
    onboardingCandidatesResult: pending(),
  };
  return compact([
    updateNorwegianCompanySearchState(state, updated),
    selectedCompany &&
      portfolioApi.findOnboardingCandidates({
        orgNo: selectedCompany.organizationnumber,
        onSuccess: (res) => [
          updateNorwegianCompanySearchState(state, {
            ...updated,
            onboardingCandidatesResult: ok(res.data),
          }),
        ],
        onFailure: () => [
          updateNorwegianCompanySearchState(state, {
            ...updated,
            onboardingCandidatesResult: failure(),
          }),
        ],
      }),
  ]);
};

export const selectSwedishCompany = (
  state: ReadyState,
  selectedCompany: SuggestedCompany | undefined,
): DCEvent[] => {
  const updated: CompanySearchDialogState = {
    ...state.swedishCompanySearchDialogState!,
    searchResult: undefined,
    selectedCompany,
  };
  return compact([
    updateSwedishCompanySearchState(state, updated),
    selectedCompany &&
      portfolioApi.findOnboardingCandidates({
        orgNo: selectedCompany.organizationnumber,
        onSuccess: (res) => [
          updateSwedishCompanySearchState(state, {
            ...updated,
            searchResult: undefined,
            onboardingCandidatesResult: ok(res.data),
          }),
        ],
        onFailure: () => [
          updateSwedishCompanySearchState(state, {
            ...updated,
            onboardingCandidatesResult: failure(),
          }),
        ],
      }),
  ]);
};

export const searchForCompany = (state: ReadyState, query: string): DCEvent[] => {
  return [
    updateNorwegianCompanySearchState(state, { searchResult: pending() }),
    portfolioApi.searchCompanies({
      query,
      onSuccess: (res) => [
        updateNorwegianCompanySearchState(state, { searchResult: ok(res.data) }),
      ],
      onFailure: () => [updateNorwegianCompanySearchState(state, { searchResult: failure() })],
    }),
  ];
};

export const sendAskForPermissionRequest = (
  state: ReadyState,
  autoonboard: boolean,
  store: PortfolioPageStore,
  dispatch: (events: DCEvent[]) => void,
  language: Language,
): DCEvent[] => {
  const searchState =
    state.norwegianCompanySearchDialogState || state.swedishCompanySearchDialogState;

  const onboardingResult = state.norwegianCompanySearchDialogState?.onboardingCandidatesResult;
  const isValid =
    autoonboard ||
    searchState?.receiverEmailAddress.validate() ||
    (isOk(onboardingResult) &&
      onboardingResult.data.isCustomer &&
      searchState?.personalNoteModel?.validate());
  const updateState: (state: ReadyState, update: Partial<CompanySearchDialogState>) => DCEvent =
    state.norwegianCompanySearchDialogState !== undefined
      ? updateNorwegianCompanySearchState
      : updateSwedishCompanySearchState;

  return isValid
    ? compact([
        updateState(state, { askForPermissionResult: pending() }),
        portfolioApi.sendAskForPermissionRequest({
          data: {
            organisationNumber: searchState?.selectedCompany?.organizationnumber ?? "",
            companyName: searchState?.selectedCompany?.name ?? "",
            email: searchState?.receiverEmailAddress.inputValue ?? "",
            autoonboard,
            requestAccessType: searchState?.accessTypeModel?.value ?? "admin",
            personalNoteText: searchState?.personalNoteModel?.validatedValue ?? "",
          },
          onSuccess: () => [
            updateState(state, { askForPermissionResult: ok() }),
            updateStore(state, {
              norwegianCompanySearchDialogState: undefined,
              swedishCompanySearchDialogState: undefined,
            }),
            ...initPortfolioCompaniesPage({
              portfolio: state.portfolio,
              dispatch,
              currentUrl: extractPortfolioCompaniesUrl(state.portfolio),
              activeHeader: state.activeHeader,
              companiesTab: state.companiesTab,
              companiesMode: state.companiesMode,
              signDocumentCounts: state.signDocumentCounts,
              store,
              language,
            }),
            showSuccess({
              key: "askForPermissionDialog.success",
              params: { companyName: searchState?.selectedCompany?.name },
            }),
          ],
          onFailure: () => [
            updateState(state, {
              askForPermissionResult: failure(),
            }),
            showError({ key: "askForPermissionDialog.error" }),
          ],
        }),
      ])
    : [];
};

export const searchSwedishBusinessRegister = (state: ReadyState): DCEvent[] => {
  const isValid = state.swedishCompanySearchDialogState?.searchField.validate();

  return isValid
    ? compact([
        updateSwedishCompanySearchState(state, { searchResult: pending() }),
        portfolioApi.searchSwedishCompanies({
          query: state.swedishCompanySearchDialogState?.searchField.inputValue ?? "",
          onSuccess: (res) => [
            updateSwedishCompanySearchState(state, {
              searchResult: ok(res.data),
              isRenderSearchResult: res.data.length > 0 && res.data.length <= 50,
              tooManyHits: res.data.length > 50,
            }),
          ],
          onFailure: () => [updateSwedishCompanySearchState(state, { searchResult: failure() })],
        }),
      ])
    : [];
};

export const toggleNorwegianCompanySearch = (state: ReadyState, flag: boolean): DCEvent[] =>
  compact([
    updateStore(state, {
      norwegianCompanySearchDialogState: flag ? createCompanySearchDialogState() : undefined,
    }),
  ]);

export const toggleSwedishCompanySearch = (state: ReadyState, flag: boolean): DCEvent[] =>
  compact([
    updateStore(state, {
      swedishCompanySearchDialogState: flag ? createCompanySearchDialogState() : undefined,
    }),
  ]);

export const registerAsCustomer = (
  state: ReadyState,
  dispatch: (events: DCEvent[]) => void,
  store: PortfolioPageStore,
  orgNo: string,
  language: Language,
): DCEvent =>
  httpPost({
    url: `/company/registerasdcompany/${orgNo}`,
    onSuccess: () => [
      ...initPortfolioCompaniesPage({
        portfolio: state.portfolio,
        dispatch,
        currentUrl: extractPortfolioCompaniesUrl(state.portfolio),
        activeHeader: state.activeHeader,
        companiesTab: "all", //activate all "tab" as the company is going to appear there
        companiesMode: state.companiesMode,
        signDocumentCounts: state.signDocumentCounts,
        store,
        language,
      }),
      showAlert({ key: "general-success" }),
    ],
    onFailure: () => [showAlert({ key: "general-error" }, "error")],
  });

function handleSelectDraftCompanyWithNoAccess(company: PortfolioCompany, state: ReadyState) {
  const selectedCompany: SuggestedCompany = {
    organizationnumber: company.organisationNumber,
    name: company.name,
  };
  const update: CompanySearchDialogState = {
    ...createCompanySearchDialogState(),
    selectedCompany,
    disableSearch: true,
    sourceOrgNoName: company.sourceOrgNoName,
    ownershipInPercent: company.ownershipInPercent,
    companyNameSearchField: new StringFieldModel({
      required: true,
      value: company.name,
    }),
    onboardingCandidatesResult: ok<OnboardingCandidateResponse>({
      organisationNumber: company.organisationNumber,
      companyName: company.name,
      namesWithRoles: compact([
        company.chairPersonName && {
          name: company.chairPersonName,
          roleTypes: [OnboardingCandidateRoleType.CHAIRPERSON],
        },
        company.ceoName && {
          name: company.ceoName,
          roleTypes: [OnboardingCandidateRoleType.CEO],
        },
      ]),
      isCustomer: company.isCustomer,
      isShareholderPortalEnabled: false,
      isBoardPortalEnabled: false,
    }),
  };
  if (isSwedishOrgNo(company.organisationNumber)) {
    return [updateStore(state, { swedishCompanySearchDialogState: update })];
  } else {
    return [updateStore(state, { norwegianCompanySearchDialogState: update })];
  }
}

export const selectCompany = (
  state: ReadyState,
  dispatch: (events: DCEvent[]) => void,
  store: PortfolioPageStore,
  company: PortfolioCompany,
  language: Language,
  setCustomRoleId?: (roleId: string) => void,
): DCEvent[] => {
  if (company.state === CompanyState.DRAFT && company.accessType === AccessType.NONE) {
    return handleSelectDraftCompanyWithNoAccess(company, state);
  }
  if (!company.isCustomer && company.accessType === AccessType.NONE) {
    return [registerAsCustomer(state, dispatch, store, company.organisationNumber, language)];
  }
  if (company.state === CompanyState.PENDING && company.id) {
    const openInvitationRequest = state.portfolioResponse.incompletedInvitationRequests.find(
      (it) => it.id === company.id,
    );
    return [updateStore(state, { openInvitationRequest })];
  }

  let url;
  const isShareholderOrRepresentative =
    company.accessType === AccessType.SHAREHOLDER ||
    company.accessType === AccessType.ADDITIONAL_REPRESENTATIVE;
  if (isShareholderOrRepresentative && company.shareholderId) {
    url = shareholderOverview(company.organisationNumber, company.shareholderId);
  } else if (company.accessType === AccessType.SUPERADMIN) {
    url = superadminUrl;
  } else if (
    company.accessType === AccessType.CUSTOM &&
    company.role &&
    company.role.customRoleId !== "all"
  ) {
    url = findInitialUrl({ role: company.role, orgNo: company.organisationNumber });
  } else if (
    company.accessType === AccessType.ADMIN &&
    company.companyId &&
    hasRegisterSubscription(state, company.organisationNumber)
  ) {
    url = adminOverview(company.companyId);
  } else {
    url = companyUrl(company.organisationNumber, company.accessType);
  }
  if (isBlank(url)) {
    logError("Blank url for company navigation in portfolio dashboard:", company);
  }
  if (company.role?.customRoleId) {
    setCustomRoleId?.(company.role.customRoleId);
  }
  return [navigate(url)];
};

const hasRegisterSubscription = (state: ReadyState, orgNo: string): boolean =>
  isRegisterSubscription(
    state.portfolioResponse.activeSubscriptionCompanies?.find(
      (s) => s.organisationNumber === orgNo,
    ),
  );

export const filterPortfolioWithText = (state: ReadyState): DCEvent[] => {
  const allCompanies = companiesInPortfolio(state.portfolioResponse);
  const notConnected = mapToNotConnectedState(
    state.portfolioResponse,
    allCompanies,
    state.notConnected.rolesFilter.value,
  );
  const query = state.portfolioSearchModel.inputValue.toLowerCase();
  return [
    updateStore(state, {
      notConnected: {
        ...notConnected,
        companies: notConnected.companies.filter(
          (c) =>
            c.name.toLowerCase().includes(query) ||
            c.organisationNumber.includes(query) ||
            c.sourceOrgNoName?.toLowerCase().includes(query),
        ),
      },
    }),
  ];
};

export const openRemoveFromPortfolioDialog = (
  state: ReadyState,
  removeCompanyFromPortfolio: PortfolioCompany,
): DCEvent[] => [updateStore(state, { removeCompanyFromPortfolio })];

export const closeRemoveFromPortfolioDialog = (state: ReadyState): DCEvent[] => [
  updateStore(state, {
    removeCompanyFromPortfolio: undefined,
    addCompaniesResult: undefined,
    removeFromPortfolioResult: undefined,
  }),
];

export const removeFromPortfolio = (state: ReadyState, onSuccess: DCEvent[]): DCEvent[] => {
  return [
    updateStore(state, { removeFromPortfolioResult: pending() }),
    portfolioApi.removeFromPortfolio({
      company: state.removeCompanyFromPortfolio!,
      onSuccess: () => [
        ...closeRemoveFromPortfolioDialog(state),
        ...onSuccess,
        showAlert({ key: "portfolio.remove-companies-success" }),
      ],
      onFailure: () => [
        updateStore(state, { removeFromPortfolioResult: failure({ key: "general-error" }) }),
      ],
    }),
  ];
};

export const openSendAdministratorInvitationDialog = (
  companyItem: CompanyItem,
  lang: Language,
): DCEvent[] => [
  {
    type: "showSendInvitationDialog",
    data: {
      invitationType: InvitationType.USER,
      organisationNumber: companyItem.organisationNumber,
      companyName: companyItem.companyName,
      lang,
      title: { key: "invite-user.title" },
      description: {
        key: "invite-user.infoMessage",
        params: { companyName: companyItem.companyName },
      },
    },
  },
];

export const openMoveToPortfolioDialog = (
  state: ReadyState,
  moveToPortfolio: PortfolioCompany,
): DCEvent[] => [updateStore(state, { moveToPortfolio })];

export const closeMoveToPortfolioDialog = (state: ReadyState): DCEvent[] => [
  updateStore(state, { moveToPortfolio: undefined }),
];

export const togglePortfolioSettings = (
  state: ReadyState | PortfolioSignaturesReadyState,
  isPortfolioSettingsOpen: boolean,
): DCEvent[] =>
  compact([
    state.portfolio.type === "portfolio-instance" &&
      (state.type === "portfolio-signatures-ready"
        ? updateSignaturesStore(state, {
            isPortfolioSettingsOpen,
            portfolioSettingsUpdateResult: undefined,
          })
        : updateStore(state, {
            isPortfolioSettingsOpen,
            portfolioSettingsUpdateResult: undefined,
          })),
  ]);

export const savePortfolioSettings = (
  state: ReadyState | PortfolioSignaturesReadyState,
  onUpdatePortfolio: (portfolio: Portfolio) => void,
): DCEvent[] => {
  return [
    state.type === "ready"
      ? updateStore(state, { portfolioSettingsUpdateResult: pending() })
      : updateSignaturesStore(state, { portfolioSettingsUpdateResult: pending() }),
    portfolioApi.updatePortfolioSettings({
      portfolioId:
        state.portfolio.type === "portfolio-instance" ? state.portfolio.portfolio.id : "",
      name: state.portfolioSettingsModel.name.validatedValue,
      onSuccess: () => {
        const portfolio: Portfolio = {
          id:
            state.type === "ready"
              ? state.portfolioResponse.portfolio!.id
              : (state.signaturesResponse?.portfolio?.id ?? ""),
          name: state.portfolioSettingsModel.name.validatedValue,
          createdAt: "",
          createdBy: "",
          ownerId: "",
          createdEmail: "",
        };
        onUpdatePortfolio(portfolio);
        return [
          state.type === "ready"
            ? updateStore(state, {
                isPortfolioSettingsOpen: false,
                portfolioSettingsUpdateResult: undefined,
                portfolioResponse: {
                  ...state.portfolioResponse,
                  portfolio,
                },
              })
            : updateSignaturesStore(state, {
                isPortfolioSettingsOpen: false,
                portfolioSettingsUpdateResult: undefined,
              }),
          showAlert({ key: "general-success" }),
        ];
      },
      onFailure: () => [
        state.type === "ready"
          ? updateStore(state, { portfolioSettingsUpdateResult: failure({ key: "general-error" }) })
          : updateSignaturesStore(state, {
              portfolioSettingsUpdateResult: failure({ key: "general-error" }),
            }),
      ],
    }),
  ];
};

export const openInviteAdminDialog = (
  lang: Language,
  portfolio: SelectedPortfolioOption,
): DCEvent[] => {
  return [
    {
      type: "showInviteNewAdminDialog",
      lang,
      portfolio,
    },
  ];
};

export const openTransferOwnershipDialog = (state: ReadyState): DCEvent[] => {
  return [
    updateStore(state, {
      showTransferOwnershipDialog: true,
      ownershipUserResult: pending(),
    }),
    portfolioApi.getPortfolioAdminsForTransfer({
      portfolio: state.portfolio,
      onSuccess: (res) => {
        const currentOwnerId = state.portfolioResponse.portfolio?.ownerId;
        const ownershipTransfer: AdminItem[] = res.data.admins.filter(
          (a) => a.id && a.id !== currentOwnerId,
        );

        return [
          updateStore(state, {
            showTransferOwnershipDialog: true,
            ownershipUserResult: ok(res.data),
            transferOwnershipModel: ownershipTransferModel(ownershipTransfer),
          }),
        ];
      },
      onFailure: () => [
        updateStore(state, {
          showTransferOwnershipDialog: true,
          ownershipUserResult: failure(),
        }),
      ],
    }),
  ];
};

const ownershipTransferModel = (
  options: AdminItem[],
): RadioFieldModel<AdminItem | undefined> | undefined => {
  return options.length >= 1
    ? new RadioFieldModel<AdminItem | undefined>({
        value: undefined,
        options: options,
        idFn: identity,
        labelFn: (item) => ({ translated: item ? `${item.name} (${item.email})` : "" }),
        required: true,
      })
    : undefined;
};

export const openExportShareholdersDialog = (
  response: PortfolioResponse,
  dispatch: PortfolioPageDispatcher,
): DCEvent[] => {
  const companies = response.userCompanies.filter((c) => c.accessType === AccessType.ADMIN);

  return [
    {
      type: "show-dialog",
      dialog: {
        type: "export-shareholders",
        model: new ExportShareholderModel(
          (data, onSuccess, onFailure) => {
            dispatch([
              portfolioApi.exportShareholderRegisterBatch({
                data,
                onSuccess: (res) => {
                  const filename = getFilenameFromHeaders(res.headers);
                  downloadFile(res.data, filename);
                  onSuccess();
                  return [];
                },
                onFailure: () => {
                  onFailure();
                  return [];
                },
              }),
            ]);
          },
          () => dispatch([{ type: "closeDialog" }]),
          companies,
          response.portfolios,
          false,
          response.portfolio?.id,
        ),
      },
    },
  ];
};

export const toggleConfirmDissolvePortfolio = (
  state: ReadyState | PortfolioSignaturesReadyState,
  flag: boolean,
): DCEvent[] => [
  state.type === "ready"
    ? updateStore(state, { confirmDissolvePortfolio: flag })
    : updateSignaturesStore(state, { confirmDissolvePortfolio: flag }),
];

export const dissolvePortfolio = (
  state: ReadyState | PortfolioSignaturesReadyState,
  onRemovePortfolio: (portfolioId: string) => void,
): DCEvent[] => {
  const portfolioId =
    state.portfolio.type === "portfolio-instance" ? state.portfolio.portfolio.id : "";
  return [
    state.type === "ready"
      ? updateStore(state, { portfolioSettingsUpdateResult: pending() })
      : updateSignaturesStore(state, { portfolioSettingsUpdateResult: pending() }),
    portfolioApi.deletePortfolio({
      portfolioId,
      onSuccess: () => {
        onRemovePortfolio(portfolioId);
        return [
          state.type === "ready"
            ? updateStore(state, {
                isPortfolioSettingsOpen: false,
                confirmDissolvePortfolio: false,
                portfolioSettingsUpdateResult: undefined,
              })
            : updateSignaturesStore(state, {
                isPortfolioSettingsOpen: false,
                confirmDissolvePortfolio: false,
                portfolioSettingsUpdateResult: undefined,
              }),
          navigate(portfolioCompaniesUrl),
          showAlert({ key: "portfolio.dissolve-portfolio.success" }),
        ];
      },
      onFailure: () => [
        state.type === "ready"
          ? updateStore(state, { portfolioSettingsUpdateResult: failure({ key: "general-error" }) })
          : updateSignaturesStore(state, {
              portfolioSettingsUpdateResult: failure({ key: "general-error" }),
            }),
      ],
    }),
  ];
};

export const setDestinationPortfolio = (
  state: ReadyState,
  destinationPortfolio: Portfolio,
): DCEvent[] => [updateStore(state, { destinationPortfolio })];

export const moveCompanyToPortfolio = (state: ReadyState): DCEvent[] => {
  return [
    updateStore(state, { moveToPortfolioResult: pending() }),
    portfolioApi.moveToPortfolio({
      company: state.moveToPortfolio!,
      portfolioId: state.destinationPortfolio!.id,
      onSuccess: () => [
        navigate(`${portfolioUrl}/${state.destinationPortfolio!.id}/companies`),
        showAlert({ key: "portfolio.add-companies-success", params: { count: 1 } }),
      ],
      onFailure: () => [
        updateStore(state, { moveToPortfolioResult: failure({ key: "general-error" }) }),
      ],
    }),
  ];
};

export const openSelectDocumentsForSigningDialog = (
  state: PortfolioSignaturesReadyState,
  fileUploadConfig: FileUploadConfig,
): DCEvent[] => {
  return [
    updateSignaturesStore(state, {
      sendForSigningState: {
        fileUploadConfig,
        sendDocumentForSigningItems: [],
        step: "select-documents",
        companiesInPortfolio: state.signaturesResponse.companiesInPortfolio,
      },
    }),
  ];
};

export const validateDocumentsForSigning = (
  state: PortfolioSignaturesReadyState,
  files: File[],
): DCEvent[] => {
  const sendForSigningState = state.sendForSigningState;
  if (sendForSigningState) {
    return [
      {
        type: "validate-files",
        files,
        fileUploadConfig: sendForSigningState.fileUploadConfig,
        onSuccess: selectDocumentsForSigning(state, files),
        onFailure: () => [
          updateSignaturesStore(state, {
            ...state,
            sendForSigningState: {
              ...sendForSigningState,
              step: "select-documents",
              message: {
                type: "warning",
                icon: "warning",
                message: { key: "signatures.alert.message.invalid-file-format" },
              },
            },
          }),
        ],
      },
    ];
  }
  return [];
};

export const selectDocumentsForSigning = (
  state: PortfolioSignaturesReadyState,
  files: File[],
): DCEvent[] => {
  const existingFilenames =
    state.sendForSigningState?.sendDocumentForSigningItems.map((i) => i.file.name) ?? [];
  const allFilenames = [...existingFilenames, ...files.map((f) => f.name)];
  const hasDuplicate = new Set(allFilenames).size !== allFilenames.length;
  if (hasDuplicate) {
    return [
      updateSignaturesStore(state, {
        ...state,
        duplicateFileUploading: { key: "signatures.send-documents-for-signing.duplicate-info" },
        sendForSigningState: {
          ...state.sendForSigningState!,
          step: "select-documents",
        },
      }),
    ];
  } else if (state.contextCompany) {
    return [
      signaturesApi.getDestinationFolders({
        orgNo: state.contextCompany,
        onSuccess: (res) => [
          updateSignaturesStore(state, {
            sendForSigningState: {
              ...state.sendForSigningState!,
              message: undefined,
              step: "select-documents",
              sendDocumentForSigningItems: [
                ...(state.sendForSigningState?.sendDocumentForSigningItems ?? []),
                ...files.map((file) => ({
                  file,
                  destinationFolderCandidates: res.data.entries,
                  companyItem: {
                    organisationNumber: state.contextCompany ?? "",
                    companyName:
                      state.sendForSigningState?.companiesInPortfolio.find(
                        (c) => c.organisationNumber === state.contextCompany,
                      )?.companyName ?? "",
                  },
                })),
              ],
            },
          }),
        ],
        onFailure: () => [showGeneralError()],
      }),
    ];
  } else {
    return [
      updateSignaturesStore(state, {
        sendForSigningState: {
          ...state.sendForSigningState!,
          message: undefined,
          step: "select-documents",
          sendDocumentForSigningItems: [
            ...(state.sendForSigningState?.sendDocumentForSigningItems ?? []),
            ...files.map((file) => ({
              file,
              destinationFolderCandidates: [],
            })),
          ],
        },
      }),
    ];
  }
};

export const openSendDocumentsForSigningDialog = ({
  state,
  aiClient,
  user,
}: {
  state: PortfolioSignaturesReadyState;
  aiClient: AIClient;
  user: User | null;
}): DCEvent[] => {
  return [
    signaturesApi.getCompanySigners({
      companies: compact(
        state.sendForSigningState!.sendDocumentForSigningItems.map(
          (c) => c.companyItem?.organisationNumber,
        ),
      ),
      onSuccess: (res, store) => {
        const signatureState =
          store.portfolioSignatures?.type === "portfolio-signatures-ready"
            ? store.portfolioSignatures
            : undefined;
        if (!signatureState || !signatureState.sendForSigningState) {
          return [];
        }
        const sendDocumentForSigningItems =
          signatureState.sendForSigningState.sendDocumentForSigningItems;
        const isBatchSigning = sendDocumentForSigningItems.length > 1;
        const destinationFolder = isBatchSigning
          ? undefined
          : sendDocumentForSigningItems.find((f) => f.destination !== undefined);
        const signersForm = new SignersForm({
          signers: [signer()],
          aiClient: aiClient,
          user: user,
          shareholders: res.data.shareholders,
          companyRoles: res.data.companyRoles,
          emailBody: getEmailBody(user, sendDocumentForSigningItems),
          selectedDestinationFolder: destinationFolder?.destination,
          isBatchSigning,
          destinationFolderModel: selectedDestinationFolderModel(
            destinationFolder?.destinationFolderCandidates,
            destinationFolder?.destination,
          ),
          showDestinationFolderSelector: true,
          showBatchEmailsSettings: isBatchSigning,
        });
        return [
          updateSignaturesStore(state, {
            sendForSigningState: {
              ...state.sendForSigningState!,
              step: "send-documents",
              signersForm,
            },
          }),
        ];
      },
      onFailure: () => {
        return [showGeneralError()];
      },
    }),
  ];
};

export const closeSignDocumentDialog = (state: PortfolioSignaturesReadyState): DCEvent[] => {
  return [updateSignaturesStore(state, { sendForSigningState: undefined })];
};

export const updateStore = (state: ReadyState, update: Partial<ReadyState>): DCEvent => ({
  type: "update-store",
  data: {
    portfolioState: {
      ...state,
      ...update,
    },
  },
});

export const updateNorwegianCompanySearchState = (
  state: ReadyState,
  update: Partial<CompanySearchDialogState>,
): DCEvent =>
  updateStore(state, {
    norwegianCompanySearchDialogState: {
      ...state.norwegianCompanySearchDialogState!,
      ...update,
    },
  });

export const updateSwedishCompanySearchState = (
  state: ReadyState,
  update: Partial<CompanySearchDialogState>,
): DCEvent =>
  updateStore(state, {
    swedishCompanySearchDialogState: {
      ...state.swedishCompanySearchDialogState!,
      ...update,
    },
  });

export type PreparedPageState =
  | { state: InvalidPortfolioIdState }
  | { state: LoadingPortfolioState; openPortfolioSettings?: () => void }
  | {
      state: ReadyState;
      actions: PortfolioPageActions;
    };

export const usePortfolio = ({
  activeHeader,
  portfolioType,
  portfolioId,
}: {
  activeHeader?: string;
  portfolioType: PortfolioType;
  portfolioId?: string;
}) => {
  const { pathname: currentUrl } = useRootStore().location;
  const userStore = useUserStore();
  const onRemovePortfolio = (portfolioId: string) => userStore.removePortfolio(portfolioId);
  const onUpdatePortfolio = (portfolio: Portfolio) => userStore.updatePortfolio(portfolio);
  const portfolio = useMemo(
    () => mapToPortfolio(portfolioType, portfolioId, userStore.portfolios),
    [portfolioId, portfolioType, userStore.portfolios],
  );

  useEffect(() => {
    runInAction(() => userStore.setPreviousLocation(currentUrl));
  }, [userStore, currentUrl]);

  const header = mapToHeader(activeHeader);

  return {
    portfolio,
    header,
    currentUrl,
    onUpdatePortfolio,
    onRemovePortfolio,
  };
};

export const mapFilesToPreviewFromSubscriptionStatus = (
  response: Ok<GetSubscriptionStatusResponse>,
): FileToPreview[] => {
  return [
    ...new Map(
      response.data.subscriptions.map((subscription) => {
        return [
          subscription.subscriberId,
          toFileToPreview(subscription.name, subscription.subscriberId),
        ];
      }),
    ).values(),
  ];
};

export const portfolioCreationInfo = (portfolio?: Portfolio): TMessage => {
  return {
    key: "portfolio.portfolio-settings.creation-info",
    params: {
      portfolioName: portfolio?.name ?? "",
      createdAt: { type: "date", value: portfolio?.createdAt ?? "" },
      createdBy: getFullName({ firstName: portfolio?.createdBy }),
    },
  };
};

export function filterCompaniesBySignature(
  tab: PortfolioSignaturesTab,
  signatures: SignaturesContext,
) {
  return new RadioFieldModel<CompanyItem | null>({
    value: null,
    options: [null, ...uniqBy(getCompanyItems(tab, signatures), "organisationNumber")],
    idFn: (option) => (option ? option.companyName : "all"),
    labelFn: (option) =>
      option ? { translated: option.companyName } : { key: "portfolio.all-companies" },
  });
}

export type FollowUpCounts = {
  signDocumentCount: number;
  followUpMattersCount: number;
  companyInfoCount: number;
  followUpShareholdersCount: number;
};

export const toFollowUpCounts = ({
  signDocumentCount,
  followUpMattersCount,
  companyInfoCount,
  followUpShareholdersCount,
}: FollowUpCounts): FollowUpCounts => ({
  signDocumentCount,
  followUpMattersCount,
  companyInfoCount,
  followUpShareholdersCount,
});

export function sumFollowUpCounts(counts: FollowUpCounts | undefined): number {
  if (!counts) return 0;
  const { signDocumentCount, followUpMattersCount, companyInfoCount, followUpShareholdersCount } =
    counts;
  return signDocumentCount + followUpMattersCount + companyInfoCount + followUpShareholdersCount;
}

export type PortfolioActions = {
  onAddAdministrator: ({ companyItem, lang }: { companyItem: CompanyItem; lang: Language }) => void;
  onNewMeeting: (organisationNumber: string, meetingType: MeetingType) => void;
  onRemoveFromPortfolio?: (company: PortfolioCompany) => void;
  onMoveToPortfolio?: (company: PortfolioCompany) => void;
  copyToClipboard: (text: string) => void;
  navigateToLastTransaction: (orgNo: string, transactionLogId: number) => void;
  onHoverLastTransaction: (transactionLogId: number) => void;
};

export const filterCompaniesWithDbotAccess = (
  companies: PortfolioCompany[],
  subscriptions: Pick<PaidSubscription, "organisationNumber" | "allowsDbot">[],
): PortfolioCompany[] =>
  companies.filter(
    (c) =>
      (c.accessType === AccessType.ADMIN ||
        (c.accessType === AccessType.CUSTOM && c.role?.modules.includes(ModuleName.DBOT))) &&
      subscriptions.some((s) => s.organisationNumber === c.organisationNumber && s.allowsDbot),
  );

export const toCompaniesWithAdminDbotAccess = (state: ReadyState): PortfolioCompany[] =>
  toCompaniesWithDbotAccess(state).filter((c) => c.accessType === AccessType.ADMIN);

export const toCompaniesWithDbotAccess = (state: ReadyState): PortfolioCompany[] =>
  filterCompaniesWithDbotAccess(
    state.userCompaniesInPortfolio,
    state.portfolioResponse.activeSubscriptionCompanies ?? [],
  );

export type SelectableCompany = {
  organisationNumber: string;
  name: string;
  portfolioId?: string;
  portfolioName?: string;
};

export type SelectablePortfolio = { id: string; name: string };

export const toSelectableCompanies = (
  items: CompanyItem[],
  userCompanies: { organisationNumber: string; portfolioId?: string; portfolioName?: string }[],
): SelectableCompany[] =>
  items.map((item) => {
    const userCompany = userCompanies.find((c) => c.organisationNumber === item.organisationNumber);
    return {
      organisationNumber: item.organisationNumber,
      name: item.companyName,
      portfolioId: userCompany?.portfolioId,
      portfolioName: userCompany?.portfolioName,
    };
  });

export const portfoliosOfCompanies = (companies: SelectableCompany[]): SelectablePortfolio[] =>
  uniqBy(
    companies.flatMap((c) =>
      c.portfolioId ? [{ id: c.portfolioId, name: c.portfolioName ?? "" }] : [],
    ),
    "id",
  );

export class CompanySelectorModel {
  private readonly getCompanies: () => SelectableCompany[];
  portfolioFilter: RadioFieldModel<SelectablePortfolio | null>;
  search = new StringFieldModel();
  showOnlySelected = new BooleanFieldModel({
    value: false,
    onChange: (isOn) => {
      if (isOn) {
        this.portfolioFilter.setValue(null);
        this.search.setInputValue("");
      }
    },
  });
  selection: OptionFieldModel<SelectableCompany>;

  constructor({
    companies,
    portfolios,
    selectedPortfolioId,
  }: {
    companies: SelectableCompany[] | (() => SelectableCompany[]);
    portfolios: SelectablePortfolio[];
    selectedPortfolioId?: string;
  }) {
    this.getCompanies = typeof companies === "function" ? companies : () => companies;
    const portfolioOptions = () =>
      portfolios.filter((p) => this.companies.some((c) => c.portfolioId === p.id));
    this.portfolioFilter = new RadioFieldModel<SelectablePortfolio | null>({
      value: portfolioOptions().find((p) => p.id === selectedPortfolioId) ?? null,
      options: () => [...portfolioOptions(), null],
      labelFn: (item) =>
        item ? { translated: item.name } : { key: "shareholders.exportContent.show-all" },
      idFn: (item) => item?.id ?? "all",
      iconFn: (item) => (item ? "suitcaseSimple" : "globe"),
    });
    this.selection = new OptionFieldModel({
      options: () => this.filteredCompanies,
      idFn: (c) => c.organisationNumber,
      labelFn: (c) => ({ translated: c.name }),
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get companies(): SelectableCompany[] {
    return this.getCompanies();
  }

  get matches(): (company: SelectableCompany) => boolean {
    const portfolioId = this.portfolioFilter.value?.id;
    const matchesSearch = orderlessPredicate<SelectableCompany>(
      (c) => [c.name, c.organisationNumber],
      this.search.inputValue,
    );
    return (c) =>
      (portfolioId === undefined || c.portfolioId === portfolioId) && matchesSearch(c, 0);
  }

  get filteredCompanies(): SelectableCompany[] {
    return this.companies.filter(
      (c) =>
        this.matches(c) &&
        (!this.showOnlySelected.value || this.selection.selectedIds.has(c.organisationNumber)),
    );
  }

  get highlightWords(): string[] {
    return tokenizeFilterString(this.search.inputValue);
  }

  get hasPortfolios(): boolean {
    return this.portfolioFilter.options.length > 1;
  }
}
